import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Users, Calendar, Briefcase, Phone, Mail, Building, Clock, Target, AlertCircle } from 'lucide-react';

const API_URL = window.location.origin || '';

const Legal = () => {
  const { currentTeam } = useTeam();
  const [projects, setProjects] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects when team changes
  useEffect(() => {
    if (currentTeam?.id) {
      fetchProjects();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  const fetchProjects = async () => {
    try {
      // Only show loading on initial load
      if (projects.length === 0) {
        setLoading(true);
      }
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects?dashboard_area=legal`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6">Legal</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to view legal information.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header with Quick Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900">Legal</h1>
          <QuickActions position="top" />
        </div>

        <Tabs defaultValue="projects" className="space-y-6">
          <TabsList>
            <TabsTrigger value="projects">
              <Briefcase className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
            <TabsTrigger value="attorneys">
              <Users className="w-4 h-4 mr-2" />
              Attorneys
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Legal Projects</h2>
                <CreateProjectDialog currentTeam={currentTeam} onProjectCreated={fetchProjects} />
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-600">Loading projects...</div>
              ) : projects.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No legal projects yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Create projects like "Create Trust", "Obtain Guardianship", "Sell House"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {projects.map(project => (
                    <ProjectCard 
                      key={project.id} 
                      project={project} 
                      currentTeam={currentTeam}
                      onProjectUpdated={fetchProjects}
                    />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Attorneys Tab */}
          <TabsContent value="attorneys">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Attorneys & Legal Contacts</h2>
                <AddAttorneyDialog attorneys={attorneys} setAttorneys={setAttorneys} />
              </div>
              {attorneys.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No attorneys added yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track multiple attorneys - Estate Planning, Elder Law, Real Estate</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {attorneys.map(attorney => (
                    <Card key={attorney.id} className="p-4 border-2">
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">{attorney.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{attorney.specialty}</p>
                      <div className="space-y-2 text-sm text-gray-700">
                        {attorney.firm && (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-500" />
                            <span>{attorney.firm}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span>{attorney.phone}</span>
                        </div>
                        {attorney.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-500" />
                            <span>{attorney.email}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Legal Documents</h2>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </div>
              <div className="text-center py-12 text-gray-900">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="font-medium">No legal documents uploaded.</p>
                <p className="text-sm mt-2 text-gray-600">Store Power of Attorney, Living Will, Guardianship papers</p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Actions - Bottom */}
        <QuickActions position="bottom" />
      </div>
    </Layout>
  );
};

// Project Card with Activity Log
const ProjectCard = ({ project, currentTeam, onProjectUpdated }) => {
  const [showActivities, setShowActivities] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newActivity, setNewActivity] = useState('');
  const [editFormData, setEditFormData] = useState({
    title: project.title || '',
    description: project.description || '',
    status: project.status || 'not_started',
    priority: project.priority || 'medium',
    target_date: project.target_date ? new Date(project.target_date).toISOString().split('T')[0] : ''
  });

  const addActivity = async () => {
    if (!newActivity.trim()) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}/activity`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ description: newActivity })
        }
      );

      if (response.ok) {
        setNewActivity('');
        toast.success('Activity logged');
        onProjectUpdated();
      } else {
        toast.error('Failed to log activity');
      }
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const updateProject = async () => {
    try {
      const updateData = {
        ...editFormData,
        target_date: editFormData.target_date ? new Date(editFormData.target_date).toISOString() : null
      };

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updateData)
        }
      );

      if (response.ok) {
        toast.success('Project updated');
        setShowEditDialog(false);
        onProjectUpdated();
      } else {
        toast.error('Failed to update project');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const deleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Project deleted');
        onProjectUpdated();
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-900 border-green-300';
      case 'in_progress': return 'bg-blue-100 text-blue-900 border-blue-300';
      case 'on_hold': return 'bg-amber-100 text-amber-900 border-amber-300';
      default: return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-900 border-red-300';
      case 'medium': return 'bg-amber-100 text-amber-900 border-amber-300';
      default: return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  return (
    <>
      <Card className="p-4 border-2">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900">{project.title}</h3>
            <div className="flex gap-2 mt-2">
              <Badge className={getStatusBadgeColor(project.status)}>
                {project.status.replace('_', ' ')}
              </Badge>
              <Badge className={getPriorityBadgeColor(project.priority)}>
                {project.priority} priority
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEditDialog(true)}
              className="text-gray-900"
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={deleteProject}
              className="text-red-600 hover:text-red-700"
            >
              Delete
            </Button>
          </div>
        </div>
        
        {project.description && (
          <p className="text-gray-700 mb-3">{project.description}</p>
        )}

        {project.target_date && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Target className="w-4 h-4" />
            <span>Target: {new Date(project.target_date).toLocaleDateString()}</span>
          </div>
        )}

        {/* Activity Log Section */}
        <div className="border-t-2 pt-3 mt-3">
          <button
            onClick={() => setShowActivities(!showActivities)}
            className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2 hover:text-blue-600"
          >
            <Clock className="w-4 h-4" />
            <span>Activity Log ({(project.activity_log || []).length})</span>
          </button>

          {showActivities && (
            <div className="space-y-3 mt-3">
              {/* Add Activity Form */}
              <div className="flex gap-2">
                <Input
                  value={newActivity}
                  onChange={(e) => setNewActivity(e.target.value)}
                  placeholder="e.g., Visited lawyer on 2/17, discussed trust details"
                  className="text-gray-900 border-2 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addActivity()}
                />
                <Button 
                  onClick={addActivity}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Log
                </Button>
              </div>

              {/* Activity Timeline */}
              {(project.activity_log || []).length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {[...(project.activity_log || [])].reverse().map((activity, idx) => (
                    <div key={idx} className="border-l-4 border-blue-600 pl-3 py-2 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {new Date(activity.date).toLocaleDateString()} {new Date(activity.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-title" className="text-gray-900">Title *</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                className="text-gray-900 border-2"
              />
            </div>

            <div>
              <Label htmlFor="edit-description" className="text-gray-900">Description</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
                className="text-gray-900 border-2"
              />
            </div>

            <div>
              <Label htmlFor="edit-status" className="text-gray-900">Status</Label>
              <Select
                value={editFormData.status}
                onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
              >
                <SelectTrigger className="text-gray-900 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-priority" className="text-gray-900">Priority</Label>
              <Select
                value={editFormData.priority}
                onValueChange={(value) => setEditFormData({ ...editFormData, priority: value })}
              >
                <SelectTrigger className="text-gray-900 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-target-date" className="text-gray-900">Target Date</Label>
              <Input
                id="edit-target-date"
                type="date"
                value={editFormData.target_date}
                onChange={(e) => setEditFormData({ ...editFormData, target_date: e.target.value })}
                className="text-gray-900 border-2"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={updateProject}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save Changes
              </Button>
              <Button
                onClick={() => setShowEditDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Create Project Dialog
const CreateProjectDialog = ({ currentTeam, onProjectCreated }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'not_started',
    priority: 'medium',
    target_date: ''
  });

  const projectTypes = [
    'Create Trust',
    'Obtain Guardianship',
    'Power of Attorney',
    'Sell House',
    'Estate Planning',
    'Healthcare Proxy',
    'Living Will',
    'Property Management',
    'Medicaid Application',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const projectData = {
        ...formData,
        dashboard_area: 'legal',
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null
      };

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(projectData)
        }
      );

      if (response.ok) {
        toast.success('Legal project created successfully');
        setOpen(false);
        setFormData({ title: '', description: '', status: 'not_started', priority: 'medium', target_date: '' });
        onProjectCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Create Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Legal Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="project-title" className="text-gray-900">Project Type *</Label>
            <select
              id="project-title"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            >
              <option value="">Select project type...</option>
              {projectTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the project"
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="status" className="text-gray-900">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="text-gray-900 border-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="priority" className="text-gray-900">Priority</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData({ ...formData, priority: value })}
            >
              <SelectTrigger className="text-gray-900 border-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="target-date" className="text-gray-900">Target Date (Optional)</Label>
            <Input
              id="target-date"
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Create Project
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Attorney Dialog
const AddAttorneyDialog = ({ attorneys, setAttorneys }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    firm: '',
    phone: '',
    email: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newAttorney = {
      id: Date.now(),
      ...formData
    };
    setAttorneys([...attorneys, newAttorney]);
    toast.success('Attorney added successfully');
    setOpen(false);
    setFormData({ name: '', specialty: '', firm: '', phone: '', email: '' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Attorney
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Attorney</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="attorney-name" className="text-gray-900">Attorney Name *</Label>
            <Input
              id="attorney-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Smith, Esq."
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="specialty" className="text-gray-900">Specialty *</Label>
            <select
              id="specialty"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              required
            >
              <option value="">Select specialty...</option>
              <option value="Estate Planning">Estate Planning</option>
              <option value="Elder Law">Elder Law</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Family Law">Family Law</option>
              <option value="Healthcare Law">Healthcare Law</option>
              <option value="General Practice">General Practice</option>
            </select>
          </div>

          <div>
            <Label htmlFor="firm" className="text-gray-900">Law Firm</Label>
            <Input
              id="firm"
              value={formData.firm}
              onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
              placeholder="Smith & Associates"
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-gray-900">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-gray-900">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="attorney@lawfirm.com"
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Add Attorney
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Legal;
